import { forwardRef, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

const variants = {
  primary:
    "bg-gradient-to-br from-[hsl(var(--primary-start))] to-[hsl(var(--primary-end))] text-primary-foreground shadow-soft hover:shadow-glow hover:-translate-y-0.5 active:translate-y-0",
  secondary: "bg-secondary text-secondary-foreground hover:bg-accent border border-border hover:-translate-y-0.5",
  outline: "border border-slate-200 bg-white text-slate-700 shadow-[0_4px_12px_-8px_rgba(15,23,42,0.3)] hover:border-primary/35 hover:bg-violet-50/60 hover:text-primary hover:-translate-y-0.5 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-accent",
  ghost: "hover:bg-accent hover:text-accent-foreground",
  danger: "bg-danger text-danger-foreground hover:bg-danger/90 shadow-xs hover:-translate-y-0.5",
  link: "text-primary underline-offset-4 hover:underline",
};

const sizes = {
  sm: "h-10 px-3.5 text-[14px] rounded-[12px] gap-1.5",
  md: "h-12 px-[20px] text-[16px] rounded-[12px] gap-2",
  lg: "h-12 px-[26px] text-[16px] rounded-[12px] gap-2",
  icon: "h-12 w-12 rounded-[12px]",
};

export const Button = forwardRef(function Button(
  { className, variant = "primary", size = "md", loading = false, disabled, onClick, children, ...props },
  ref
) {
  const [ripples, setRipples] = useState([]);
  const rippleId = useRef(0);
  const prefersReducedMotion = usePrefersReducedMotion();

  // A ripple per click, not per component -- keyed and removed once its own
  // animation ends, so rapid double-clicks stack cleanly instead of one
  // click's fade cutting another's short. Skipped for the `link` variant
  // (a ripple on plain inline text reads as a rendering glitch, not a
  // button press) and for reduced motion.
  const handleClick = (e) => {
    if (!prefersReducedMotion && variant !== "link") {
      const rect = e.currentTarget.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height) * 1.6;
      const id = rippleId.current++;
      setRipples((prev) => [
        ...prev,
        { id, x: e.clientX - rect.left - size / 2, y: e.clientY - rect.top - size / 2, size },
      ]);
    }
    onClick?.(e);
  };

  return (
    <button
      data-ui="button"
      data-size={size}
      data-variant={variant}
      ref={ref}
      onClick={handleClick}
      // A loading button that stays clickable submits twice. In payroll that is
      // not a cosmetic bug.
      disabled={disabled || loading}
      className={cn(
        "relative inline-flex items-center justify-center overflow-hidden whitespace-nowrap font-bold tracking-[-0.01em] transition-all duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:pointer-events-none disabled:opacity-50",
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      <AnimatePresence>
        {ripples.map((r) => (
          <motion.span
            key={r.id}
            initial={{ opacity: 0.35, scale: 0 }}
            animate={{ opacity: 0, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            onAnimationComplete={() => setRipples((prev) => prev.filter((p) => p.id !== r.id))}
            className="pointer-events-none absolute rounded-full bg-current"
            style={{ left: r.x, top: r.y, width: r.size, height: r.size }}
            aria-hidden="true"
          />
        ))}
      </AnimatePresence>
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
      {children}
    </button>
  );
});
